import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CameraState,
  CoincidentAggregation,
  GridResolution,
  XyzDataPoint,
  ZInterpolation,
} from '../types';
import {
  DEFAULT_SURFACE_CHART_INPUTS,
  DEFAULT_SURFACE_CHART_STYLE,
  SurfaceChartController,
  type SurfaceChartInputs,
  type SurfaceChartStyle,
  type SurfaceMarks,
} from '../controller/surfaceChartController';
import type { CollapsedSample } from '../data/collapseCoincident';
import type { HorizontalAxes } from '../data/horizontalAxes';
import { MarkList, type MarkListColors } from '../interaction/MarkList';
import { useMarkFocus } from '../interaction/useMarkFocus';
import type { SequentialRampName } from '../render/colorScale';
import type { SurfaceColoring } from '../render/surfacePaint';
import type { LightingModel } from '../render/lighting';
import { toListedSample, toSurfaceListItems } from './pointMarkItems';

export type Surface3DStyle = SurfaceChartStyle;
export const DEFAULT_SURFACE_3D_STYLE = DEFAULT_SURFACE_CHART_STYLE;

export type Surface3DChartProps = {
  points: XyzDataPoint[];
  /**
   * The two horizontal axes, decided by the host. See `SurfaceChartInputs['axes']`.
   *
   * Required rather than derived from `points`, so the four XYZ views can be made to agree about
   * the plane they share. `toDataHorizontalAxes` is the baseline for a caller with no opinion.
   */
  axes: HorizontalAxes;
  xTitle: string;
  yTitle: string;
  zTitle: string;
  interpolation: ZInterpolation;
  aggregation: CoincidentAggregation;
  resolution: GridResolution;
  ramp: SequentialRampName;
  bands: number;
  /** What colors the surface. The height, through `ramp` and `bands`, when absent. */
  coloring?: SurfaceColoring;
  contourCount: number;
  style?: Partial<Surface3DStyle>;
  camera?: CameraState;
  lighting?: LightingModel;
  /**
   * Which renderer draws the fill. `auto` prefers the GPU and falls back on its own.
   *
   * The two produce the same image - compared at a mean channel difference of 0.13 out of 255 over
   * a whole frame - so this is a performance choice, not a visual one.
   */
  renderer?: SurfaceChartInputs['renderer'];
  /** Fraction of the viewport the software path rasterises at. Ignored on the GPU path. */
  rasterScale?: number;
  format: (value: number) => string;
  surfaceColor: string;
  inkColor: string;
  gridColor: string;
  /** The axis box's faces. See `SurfaceChartInputs['panelColor']`. */
  panelColor: string;
  onHoverSample?: SurfaceChartInputs['onHoverSample'];
  /** A press that did not travel over a sample or a column, or Enter on its row in the mark list. */
  onClickSample?: (sample: CollapsedSample) => void;
  /** The mark list's accessible name and visible heading, worded by the host. */
  markListLabel: string;
  /**
   * Notified when the chart gains or loses something to draw.
   *
   * Not the same question as "are there points": a field pair that puts every component on one line
   * gives plenty of points and no surface. A host that offers an export needs this, because the
   * layers keep the last surface they painted and only the chart knows they are stale.
   */
  onDrawableChange?: (isDrawable: boolean) => void;
  emptyState?: React.ReactNode;
  /**
   * Publishes the chart's export hook when the controller is built, and null when it is torn down.
   *
   * A callback rather than a ref the host fills, so the host cannot hold a hook that outlives the
   * controller it reads — the canvases go with `destroy()`.
   */
  onExportCanvasAvailable?: (
    createExportCanvas: (() => OffscreenCanvas | null) | null
  ) => void;
};

/**
 * A thin shell over {@link SurfaceChartController}.
 *
 * React's only jobs here are owning the container element and forwarding props. Everything with a
 * lifetime - the canvases, the camera, the GPU device, the derived grid and mesh, the frame
 * scheduling - belongs to the controller, which invalidates exactly what each change affects rather
 * than inferring it from a dependency array. The array this replaced had twenty-one entries and was
 * the direct cause of two bugs.
 */
export const Surface3DChart = ({
  points,
  xTitle,
  yTitle,
  zTitle,
  axes,
  interpolation,
  aggregation,
  resolution,
  ramp,
  bands,
  coloring,
  contourCount,
  style,
  camera,
  lighting,
  renderer,
  rasterScale,
  format,
  surfaceColor,
  inkColor,
  gridColor,
  panelColor,
  onHoverSample,
  onClickSample,
  markListLabel,
  onDrawableChange,
  emptyState,
  onExportCanvasAvailable,
}: Surface3DChartProps) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const controllerRef = useRef<SurfaceChartController | null>(null);

  /* Held in a ref, and deliberately not a dependency below. The effect's cleanup destroys the
     controller, so keying it on this callback would rebuild the chart — camera reset, capability
     detection re-run, mesh rebuilt — on every render that passes a fresh inline function, which is
     the ordinary way to pass an optional callback to an exported component. It is a notification
     sink rather than an input to the controller, so it has no business invalidating one. */
  const onExportCanvasAvailableRef = useRef(onExportCanvasAvailable);
  onExportCanvasAvailableRef.current = onExportCanvasAvailable;

  useEffect(() => {
    if (!container) return;
    const controller = new SurfaceChartController(container);
    controllerRef.current = controller;
    onExportCanvasAvailableRef.current?.(() => controller.createExportCanvas());

    return () => {
      onExportCanvasAvailableRef.current?.(null);
      controller.destroy();
      controllerRef.current = null;
    };
  }, [container]);

  const resolvedStyle = useMemo(
    () => ({ ...DEFAULT_SURFACE_CHART_STYLE, ...style }),
    [style]
  );

  /* Whether there is anything drawn, as the controller sees it - not `points.length === 0`. A
     field pair that puts every component on one line gives plenty of points and no surface, and
     that case is the one the gridding goes to trouble to detect. Seeded from the point count so
     the first frame has an answer before the controller has produced one. */
  const [isDrawable, setIsDrawable] = useState(points.length > 0);
  const isEmpty = !isDrawable;

  /* Held in a ref and wrapped in a stable callback for the same reason as `onExportCanvasAvailable`:
     it is a notification sink, and an inline callback in the dependency list below would re-send
     every input on every render of the host. */
  const onDrawableChangeRef = useRef(onDrawableChange);
  onDrawableChangeRef.current = onDrawableChange;

  const handleDrawableChange = useCallback((next: boolean) => {
    setIsDrawable(next);
    onDrawableChangeRef.current?.(next);
  }, []);

  const [marks, setMarks] = useState<SurfaceMarks | null>(null);
  const markListItems = useMemo(
    () =>
      marks === null
        ? []
        : toSurfaceListItems({ marks, xTitle, yTitle, zTitle, format }),
    [marks, xTitle, yTitle, zTitle, format]
  );
  const markIds = useMemo(
    () => markListItems.map(({ id }) => id),
    [markListItems]
  );
  const { activeId, setActiveId, highlightedId, containerFocusProps } =
    useMarkFocus(markIds);

  const onClickSampleRef = useRef(onClickSample);
  onClickSampleRef.current = onClickSample;
  const handleClickSample = useCallback(
    (sample: CollapsedSample) => onClickSampleRef.current?.(sample),
    []
  );
  const activateListedMark = useCallback(
    (id: string) => {
      const sample = marks && toListedSample(marks, id);
      if (sample) onClickSampleRef.current?.(sample);
    },
    [marks]
  );

  const markListColors = useMemo<MarkListColors>(
    () => ({
      surface: surfaceColor,
      ink: inkColor,
      mutedInk: inkColor,
      border: gridColor,
    }),
    [surfaceColor, inkColor, gridColor]
  );

  /* One effect, and its dependency list is the props themselves rather than a hand-maintained
     account of what depends on what. Recomputation is the controller's problem, and it has the
     information to decide. */
  useEffect(() => {
    controllerRef.current?.setInputs({
      points,
      axes,
      interpolation,
      aggregation,
      resolution,
      ramp,
      bands,
      coloring: coloring ?? DEFAULT_SURFACE_CHART_INPUTS.coloring,
      contourCount,
      style: resolvedStyle,
      camera: camera ?? DEFAULT_SURFACE_CHART_INPUTS.camera,
      lighting: lighting ?? DEFAULT_SURFACE_CHART_INPUTS.lighting,
      xTitle,
      yTitle,
      zTitle,
      surfaceColor,
      inkColor,
      gridColor,
      panelColor,
      format,
      rasterScale: rasterScale ?? 1,
      renderer: renderer ?? 'auto',
      onHoverSample,
      litId: highlightedId,
      onClickSample: handleClickSample,
      onMarksChange: setMarks,
      onDrawableChange: handleDrawableChange,
    });
  }, [
    container,
    points,
    axes,
    interpolation,
    aggregation,
    resolution,
    ramp,
    bands,
    coloring,
    contourCount,
    resolvedStyle,
    camera,
    lighting,
    xTitle,
    yTitle,
    zTitle,
    surfaceColor,
    inkColor,
    gridColor,
    panelColor,
    format,
    rasterScale,
    renderer,
    onHoverSample,
    highlightedId,
    handleClickSample,
    handleDrawableChange,
  ]);

  return (
    <div
      {...containerFocusProps}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        background: surfaceColor,
      }}
    >
      <div
        ref={setContainer}
        style={{ width: '100%', height: '100%', position: 'relative' }}
      >
        {isEmpty && emptyState}
      </div>
      {!isEmpty && markListItems.length > 0 && (
        <MarkList
          kind="list"
          label={markListLabel}
          items={markListItems}
          activeId={activeId}
          onActiveChange={setActiveId}
          onActivate={activateListedMark}
          colors={markListColors}
        />
      )}
    </div>
  );
};
